// Sync-related modal dialogs: first-sync prompt and conflict resolver.

import { el, clear } from './dom.js';
import { openSheet } from './sheet.js';

function openModal({ title, body, footer, onClose }) {
  const footerEl = el('div', { class: 'modal-footer-row' }, footer);
  const bodyWrap = el('div', {}, [body, footerEl]);
  const handle = openSheet({
    kind: 'fullscreen',
    title,
    body: bodyWrap,
    onDismiss: onClose,
  });
  return { close: () => handle.close() };
}

/**
 * First-sync prompt: remote vault exists but this device has no shared history.
 * Offer Pull-with-password, Push (overwrite remote), or Skip.
 */
export function openFirstSyncDialog({ remote, onPull, onPushOverwrite, onSkip }) {
  let dlg = null;
  const pwInput = el('input', { type: 'password', placeholder: 'Master password', autocomplete: 'current-password', style: { marginTop: '8px' } });
  const status = el('div', { class: 'error', style: { color: 'var(--danger)', fontSize: '13px', minHeight: '1em', marginTop: '6px' } });
  const body = el('div', {}, [
    el('p', {}, [`A vault was found on Puter (modified ${formatTime(remote.modifiedAt)}, ${formatBytes(remote.size)}). This device has no sync history.`]),
    el('p', { style: { color: 'var(--muted)', fontSize: '13px' } }, ['To join the cloud vault, enter the master password it was created with.']),
    pwInput,
    status,
  ]);

  const pullBtn = el('button', { class: 'primary', onclick: async () => {
    status.textContent = '';
    if (!pwInput.value) { status.textContent = 'Enter the master password.'; return; }
    pullBtn.disabled = true; pullBtn.textContent = 'Pulling…';
    try { await onPull(pwInput.value); skipped = true; dlg.close(); }
    catch (err) {
      status.textContent = err?.name === 'DecryptionError'
        ? 'Wrong password for the cloud vault.'
        : (err?.message ?? String(err));
      pullBtn.disabled = false; pullBtn.textContent = 'Pull from cloud';
    }
  } }, ['Pull from cloud']);

  // Skip must also run when the sheet is dismissed by Escape or the back
  // button, otherwise the sync indicator stays stuck in 'first-sync-required'.
  // Guarded because closing via the Skip button also fires onDismiss.
  let skipped = false;
  const skipOnce = () => { if (skipped) return; skipped = true; onSkip?.(); };

  dlg = openModal({
    title: 'Cloud Safe found',
    body,
    footer: [
      el('button', { class: 'ghost', onclick: () => { dlg.close(); skipOnce(); } }, ['Skip']),
      el('button', { class: 'ghost danger', onclick: async () => {
        if (!confirm('Overwrite the cloud Safe with this device\'s data?')) return;
        await onPushOverwrite();
        skipped = true;
        dlg.close();
      } }, ['Overwrite cloud']),
      pullBtn,
    ],
    onClose: skipOnce,
  });
  setTimeout(() => pwInput.focus(), 0);
  return dlg;
}

/**
 * Conflict modal — both sides changed since the last sync.
 */
export function openConflictDialog({ local, remote, onUseLocal, onUseRemote, onDownloadRemote }) {
  let dlg = null;
  const body = el('div', {}, [
    el('p', {}, ['Both this device and the cloud changed since the last sync. Pick which version to keep — the other will be overwritten.']),
    el('div', { class: 'conflict-grid' }, [
      el('div', { class: 'conflict-card' }, [
        el('div', { class: 'label' }, ['This device']),
        el('div', { class: 'time' }, [formatTime(local.modifiedAt)]),
      ]),
      el('div', { class: 'conflict-card' }, [
        el('div', { class: 'label' }, ['Cloud']),
        el('div', { class: 'time' }, [formatTime(remote.modifiedAt)]),
        el('div', { class: 'size' }, [formatBytes(remote.size)]),
      ]),
    ]),
    el('p', { style: { color: 'var(--muted)', fontSize: '13px', marginTop: '8px' } }, [
      'Not sure? Download the cloud copy as a .enc backup, then use the recovery tool to inspect it before choosing.',
    ]),
  ]);
  dlg = openModal({
    title: 'Sync conflict',
    body,
    footer: [
      el('button', { class: 'ghost', onclick: async () => { await onDownloadRemote(); } }, ['Download cloud as .enc']),
      el('button', { onclick: async () => { await onUseRemote(); dlg.close(); } }, ['Use cloud']),
      el('button', { class: 'primary', onclick: async () => { await onUseLocal(); dlg.close(); } }, ['Use this device']),
    ],
  });
  return dlg;
}

function formatTime(ms) {
  if (!ms) return 'unknown time';
  return new Date(ms).toLocaleString();
}
function formatBytes(n) {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
